import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Grid, Card, CardMedia, CardContent, CardActions, Dialog, DialogTitle, DialogContent, DialogActions, TextField, CircularProgress, Chip, IconButton
} from '@mui/material';
import { getMyTeams, deleteTeam } from '../api';
import GroupIcon from '@mui/icons-material/Group';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import TeamDetailView from './TeamDetailView';
import CreateTeamWithBilling from '../components/CreateTeamWithBilling';

function TeamCard({ team, onDelete, onClick }) {
  return (
    <Card 
      sx={{ 
        maxWidth: 280, 
        minWidth: 260, 
        m: 1, 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'space-between',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: 3,
          transform: 'translateY(-2px)',
          transition: 'all 0.2s ease-in-out'
        }
      }}
      onClick={onClick}
    >
      {team.image_url ? (
        <CardMedia component="img" height="140" image={team.image_url} alt={team.name} />
      ) : (
        <Box sx={{ height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5' }}>
          <GroupIcon sx={{ fontSize: 60, color: '#bbb' }} />
        </Box>
      )}
      <CardContent>
        <Typography variant="h6" noWrap>{team.name}</Typography>
        {team.description && <Typography variant="body2" color="text.secondary" sx={{ mt: 1, minHeight: 40, maxHeight: 40, overflow: 'scroll' }}>{team.description}</Typography>}
      </CardContent>
      <CardActions sx={{ justifyContent: 'space-between', pb: 2, px: 2 }}>
        {team.status === 'approved' ? (
          <Chip label="Approved" color="success" />
        ) : (
          <Chip label={team.status} color="warning" />
        )}
        {team.role === 'coach' && (
          <IconButton 
            color="error" 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              onDelete(team);
            }}
            sx={{ ml: 'auto' }}
          >
            <DeleteIcon />
          </IconButton>
        )}
      </CardActions>
    </Card>
  );
}

function DeleteTeamModal({ open, onClose, onConfirm, team, deleting }) {
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setConfirmation('');
      setError('');
    }
  }, [open]);

  const handleConfirm = () => {
    if (confirmation !== team?.name) {
      setError('Please type the exact team name to confirm deletion.');
      return;
    }
    onConfirm();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Delete Team</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography>
            Are you sure you want to delete <strong>{team?.name}</strong>? This action cannot be undone.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            All team memberships and related data will be permanently removed.
          </Typography>
          <TextField
            label={`Type "${team?.name}" to confirm`}
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            fullWidth
            autoFocus
          />
          {error && <Typography color="error">{error}</Typography>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleting}>Cancel</Button>
        <Button 
          onClick={handleConfirm} 
          variant="contained" 
          color="error" 
          disabled={deleting || confirmation !== team?.name}
        >
          {deleting ? <CircularProgress size={22} color="inherit" /> : 'Delete Team'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function CoachTeamsSection() {
  const [myTeams, setMyTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deletingTeam, setDeletingTeam] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);

  const fetchTeams = async () => {
    setLoading(true);
    const my = await getMyTeams();
    setMyTeams(my);
    setLoading(false);
  };

  useEffect(() => { fetchTeams(); }, []);

  const handleCreateTeam = () => setShowCreateTeam(true);

  const handleDelete = (team) => {
    setDeletingTeam(team);
    setDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await deleteTeam(deletingTeam.id);
      await fetchTeams();
      setDeleteModal(false);
      setDeletingTeam(null);
    } catch (err) {
      console.error('Failed to delete team:', err);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteClose = () => {
    setDeleteModal(false);
    setDeletingTeam(null);
  };

  const handleTeamClick = (team) => {
    setSelectedTeam(team);
  };

  const handleBackToTeams = () => {
    setSelectedTeam(null);
  };

  if (selectedTeam) {
    return (
      <TeamDetailView 
        team={selectedTeam} 
        onBack={handleBackToTeams}
        onTeamDeleted={() => {
          setSelectedTeam(null);
          fetchTeams();
        }}
      />
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">My Teams</Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={handleCreateTeam}
        >
          Create Team
        </Button>
      </Box>
      {loading ? <CircularProgress /> : (
        <Grid container spacing={2}>
          {myTeams.length === 0 ? (
            <Typography sx={{ ml: 2 }}>You are not a member of any teams yet.</Typography>
          ) : myTeams.map(team => (
            <Grid item key={team.id}>
              <TeamCard 
                team={team} 
                onDelete={handleDelete} 
                onClick={() => handleTeamClick(team)}
              />
            </Grid>
          ))}
        </Grid>
      )}
      
      {/* Create Team Dialog */}
      <Dialog 
        open={showCreateTeam} 
        onClose={() => setShowCreateTeam(false)}
        maxWidth="lg"
        fullWidth
      >
        <CreateTeamWithBilling
          onSuccess={(newTeam) => {
            setShowCreateTeam(false);
            fetchTeams();
          }}
          onCancel={() => setShowCreateTeam(false)}
        />
      </Dialog>
      
      <DeleteTeamModal 
        open={deleteModal} 
        onClose={handleDeleteClose} 
        onConfirm={handleDeleteConfirm} 
        team={deletingTeam} 
        deleting={deleting} 
      />
    </Box>
  );
} 